import { Injectable, inject, signal } from "@angular/core";
import { ColumnVO, IServerSideDatasource, IServerSideGetRowsParams, IServerSideGetRowsRequest, SortModelItem } from "ag-grid-community";
import { AgGridgroupService } from "./ag-grid-group.service";
import { AgGridGroupStore } from "./ag-grid-group.store";
import { APP_CONFIG } from "app.config";
import { STATUS_IN_DATABASE, STATUS_IN_DB } from "app/shared/common/constants/Status.constant";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";
import { FuseLoadingService } from "@fuse/services/loading";
import { RequestDefault } from "./ag-grid-group.constant";
import { catchError, Observable, switchMap, tap } from "rxjs";

@Injectable()
export class AgGridGroupDataSource implements IServerSideDatasource {
  public AGGRID_GROUP_API = {
    API_PAGING: `${APP_CONFIG.MCRHRC}md/vHrtbCompany/pivotPaging`,
    API_COUNT: `${APP_CONFIG.MCRHRC}md/vHrtbCompany/pivotCount`,
    UPDATE_RECORD_STATUS: `${APP_CONFIG.MCRHRC}md/vHrtbCompany/updateRecordStatus`,
  };
  public requisitionId: any;
  public actionTypeId: any;
  public actionId: any;
  public existedIds: any;
  public questionnaireCode: any;
  public surveyFlg: boolean = false;
  public taskIds: any;
  public fieldAggId: any;
  public values: any;
  public flowType: any;
  public savedListId: number;
  public matchingProcess: string;
  public notRequestCode: any;
  public triggerCustomReport: boolean = false; // Dùng để trigger custom report
  protected multipleFtsSearch = signal(false);

  protected extraFtsSearchValue = []; // Field này dùng để cho nơi nào cần override để add thêm cái custom fulltext search theo nhiều ftsValue nha

  protected aggridGroupStore: AgGridGroupStore = inject(AgGridGroupStore);
  protected agGridGroupService: AgGridgroupService = inject(AgGridgroupService);
  protected loadingService: FuseLoadingService = inject(FuseLoadingService);

  public getRows(params: IServerSideGetRowsParams) {
    this.aggridGroupStore.loading$.next(true);
    const { request } = params;

    const statusFilterValue = (request?.filterModel as any)?.recordStatus?.values?.length > 0 ? (request?.filterModel as any)?.recordStatus?.values[0] : null;
    if (statusFilterValue) {
      if (statusFilterValue === STATUS_IN_DB.ACTIVE) {
        (request.filterModel as any).recordStatus.values[0] =
          STATUS_IN_DATABASE.ACTIVE;
      } else {
        (request.filterModel as any).recordStatus.values[0] =
          STATUS_IN_DATABASE.UNACTIVE;
      }
    }

    if (this.multipleFtsSearch()) {
      this.updateApiUrl();
    }

    this.mutateRequestBeforeExecute(params);
    let _request = UtilCommon.deepClone<IServerSideGetRowsRequest>(
      params.request
    );

    let requestWithFtsSearch = {
      ..._request
    };

    if (this.multipleFtsSearch() && (this.aggridGroupStore?.searchKeyword?.length || this.aggridGroupStore?.advancedFilter)) {
      requestWithFtsSearch['filterModel'] = this.aggridGroupStore?.advancedFilter || {};
      requestWithFtsSearch['fields'] = this.pickedFieds();
      requestWithFtsSearch['fullText'] = this.aggridGroupStore.searchKeyword;
    } else {
      requestWithFtsSearch = this.aggridGroupStore.buildFullTexSearchRequest({
        ..._request,
      },
        this.extraFtsSearchValue
      );
    }
    let requestAfterTransform = this.aggridGroupStore.transformRequestBeforeQuery(requestWithFtsSearch);

    const requests$: Observable<any>[] = [
      this.agGridGroupService.pivotPagingObservable(
        requestAfterTransform,
        this.AGGRID_GROUP_API.API_PAGING
      ),
      this.agGridGroupService.pivotCountObservable(
        requestAfterTransform,
        this.AGGRID_GROUP_API.API_COUNT
      ),
    ];

    this.executeCallApiObs(params, requests$);
  }

  protected resolveRowCol(pagingResult, countResult) {
    return {
      rowData: pagingResult,
      rowCount: countResult ?? 0,
      count: countResult,
    }
  }

  public pivotCountCache: number | null = null; // Store pivotCount result

  protected executeCallApiObs(params: IServerSideGetRowsParams, requests$: Observable<any>[]) {
    if (params?.request?.startRow === 0) {
      this.pivotCountCache = null;
    }
    this.loadingService.show();

    const [pagingRequest$, countRequest$] = requests$;

    // Gọi paging trước, sau khi xong mới gọi count
    pagingRequest$.pipe(
      tap((pagingResponse) => {
        let rowData = [];
        if (pagingResponse?.data?.length > 0) {
          let index = params.request.startRow + 1;
          rowData = pagingResponse.data.map(artifact => ({
            ...artifact,
            no: index++,
            id: artifact.id || UtilCommon.generateUUID()
          }));
        }
        const rowCount = pagingResponse?.lastRow ?? this.pivotCountCache ?? 0;
        params.success({ rowData, rowCount });
        this.loadingService.hide();
        this.aggridGroupStore.refreshTrigger$.next(true);
      }),
      catchError((error) => {
        console.error("Error loading paging data:", error);
        params.success({ rowData: [], rowCount: 0 });
        this.loadingService.hide();
        this.aggridGroupStore.refreshTrigger$.next(true);
        return [];
      }),
      // Sau khi paging xong, mới gọi count nếu cần
      switchMap(() => {
        if (this.pivotCountCache === null) {
          return countRequest$.pipe(
            tap((countResponse) => {
              this.pivotCountCache = countResponse?.data ?? 0;
              this.aggridGroupStore.count$.next(`(${this.pivotCountCache})`);
              params.api?.setRowCount(this.pivotCountCache, true);
            }),
            catchError((error) => {
              console.error("Error loading row count:", error);
              this.aggridGroupStore.count$.next("(0)");
              params.api?.setRowCount(0, true);
              return [];
            })
          );
        } else {
          this.aggridGroupStore.count$.next(`(${this.pivotCountCache})`);
          return [];
        }
      })
    ).subscribe();
  }

  protected executeCallApiObsBackup(params: IServerSideGetRowsParams, requests$: Observable<any>[]) {
    if (params?.request?.startRow === 0) {
      this.pivotCountCache = null;
    }
    this.loadingService.show();

    const [pagingRequest$, countRequest$] = requests$;

    // Handle paging request (updates grid immediately)
    pagingRequest$.pipe(
      tap((pagingResponse) => {
        let rowData = [];
        if (pagingResponse?.data?.length > 0) {
          let index = params.request.startRow + 1;
          rowData = pagingResponse.data.map(artifact => ({
            ...artifact,
            no: index++,
            id: artifact.id || UtilCommon.generateUUID() // Ensure ID exists
          }));
        }

        // Use cached count if available, else use lastRow or default to 0
        const rowCount = pagingResponse?.lastRow ?? this.pivotCountCache ?? 0;

        // ✅ Update AG Grid immediately with available data
        // console.log(rowData, rowCount,'ddđ');

        params.success({ rowData, rowCount });

        this.loadingService.hide();
        this.aggridGroupStore.refreshTrigger$.next(true);
      }),
      // take(1),
      catchError((error) => {
        console.error("Error loading paging data:", error);
        params.success({ rowData: [], rowCount: 0 });
        this.loadingService.hide();
        this.aggridGroupStore.refreshTrigger$.next(true);
        return error;
      })
    ).subscribe();

    // Handle count request (only fetch if not already stored)
    if (this.pivotCountCache === null) {
      countRequest$.pipe(
        tap((countResponse) => {
          this.pivotCountCache = countResponse?.data ?? 0; // Store count result
          this.aggridGroupStore.count$.next(`(${this.pivotCountCache})`);

          // ✅ Update AG Grid with correct total row count
          // params.api?.refreshServerSide({ purge: false }); // Refresh grid to reflect new rowCount
          params.api?.setRowCount(this.pivotCountCache, true);
        }),
        // take(1),
        catchError((error) => {
          console.error("Error loading row count:", error);
          this.aggridGroupStore.count$.next("(0)");
          params.api?.setRowCount(0, true);
          return error;
        })
      ).subscribe();
    } else {
      // Use cached value immediately
      this.aggridGroupStore.count$.next(`(${this.pivotCountCache})`);
    }
  }

  addFieldToItems(
    items: any[],
    fieldName: string = "key",
    fieldValue: any
  ): any[] {
    return items.map((item) => ({
      ...item,
      [fieldName]: fieldValue,
    }));
  }

  protected filterSortModel(request: IServerSideGetRowsRequest) {
    if (request.sortModel && request.sortModel.length === 0) {
      request.sortModel = this.getDefaultSortModel();
    } else if (request.sortModel.length) {
      request.sortModel = request.sortModel.filter(
        (item) => item.colId !== "ag-Grid-AutoColumn"
      )?.concat(this.getDefaultSortModel());
    }
  }

  protected mutateRequestBeforeExecute(params: IServerSideGetRowsParams) {
    const request: IServerSideGetRowsRequest = params.request;
    request.pivotMode = request?.rowGroupCols.length == 0;
    if (request.rowGroupCols?.length) {
      params.request.valueCols = RequestDefault
    }

    this.filterSortModel(request);

    if (request?.groupKeys?.length) {
      let requestGroupKeys: any[] = UtilCommon.deepClone(request.groupKeys);

      request.groupKeys =
        request?.groupKeys
          .reduce((prev, curr: any) => {
            return prev?.concat(curr?.id || curr);
          }, [])
        || [];

      let newRowGroupCols: ColumnVO[] = UtilCommon.deepClone(
        request.rowGroupCols
      );

      newRowGroupCols.forEach((item, index) => {
        let groupKeyCurr = requestGroupKeys[index];
        if (typeof groupKeyCurr == "object" && groupKeyCurr && Object.keys(groupKeyCurr)?.length) {
          item.field = `${item.field}Id`;
        }

        if (requestGroupKeys.length == newRowGroupCols.length) {
          if (!groupKeyCurr) {
            request.groupKeys[index] = null;
          }
        }
      });
      newRowGroupCols?.length && (request.rowGroupCols = newRowGroupCols);
    }
  }

  protected getDefaultSortModel(): SortModelItem[] {
    return [
      {
        colId: "id",
        sort: "desc",
      },
    ];
  }

  // Trường hợp mutilple search nhìu table tren 1 view nên cần phải nhét các field cần lấy về (BE ràng buộc ?!)
  protected pickedFieds() { return [] }

  protected updateApiUrl() { }
}
